import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Coins, User, LogIn } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import LanguageSelector from '../ui/LanguageSelector';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const { isAuthenticated, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const goToLogin = () => {
    navigate('/login');
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      
      // Calculate scroll progress
      const winScroll = document.body.scrollTop || document.documentElement.scrollTop;
      const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrolled = (winScroll / height) * 100;
      setScrollProgress(scrolled);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when screen resizes to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const navItems = [
    { id: 'why-gold', labelKey: 'whyGold' },
    { id: 'performance', labelKey: 'performance' },
    { id: 'calculator', labelKey: 'calculator' },
    { id: 'sources', labelKey: 'sources' }
  ];

  const menuVariants = {
    closed: {
      opacity: 0,
      height: 0,
      transition: {
        duration: 0.3,
        when: "afterChildren"
      }
    },
    open: {
      opacity: 1,
      height: "auto",
      transition: {
        duration: 0.3,
        when: "beforeChildren",
        staggerChildren: 0.05
      }
    }
  };

  const itemVariants = {
    closed: { opacity: 0, x: -20 },
    open: { opacity: 1, x: 0 }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'glass-dark shadow-lg shadow-black/20' 
          : 'bg-transparent'
      }`}
    >
      {/* Scroll Progress Bar */}
      <motion.div 
        className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-[#D4AF37] to-[#F5ECD7]"
        style={{ width: `${scrollProgress}%` }}
        initial={{ width: 0 }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <motion.a 
            href="/" 
            className="flex items-center gap-2 group"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <motion.div 
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#B8960C] flex items-center justify-center shadow-lg shadow-[#D4AF37]/20 group-hover:shadow-[#D4AF37]/40 transition-all duration-300"
              whileHover={{ rotate: 5 }}
            >
              <Coins className="w-4 h-4 sm:w-5 sm:h-5 text-black" />
            </motion.div>
            <span className="font-bold text-lg sm:text-xl text-white">
              BuyMe<span className="text-[#D4AF37]">Gold</span>
            </span>
          </motion.a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6 lg:gap-8">
            {navItems.map((item, index) => (
              <motion.button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="relative text-sm font-medium text-gray-400 hover:text-white transition-colors group py-2"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -2 }}
              >
                {t(item.labelKey)}
                <motion.span 
                  className="absolute -bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[#D4AF37] to-[#B8960C] group-hover:w-full transition-all duration-300"
                  layoutId={`underline-${item.id}`}
                />
              </motion.button>
            ))}
          </nav>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <LanguageSelector variant="minimal" />
            {isAuthenticated ? (
              <motion.div
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="btn-gold text-black font-semibold px-5 lg:px-6 py-2.5 rounded-xl text-sm flex items-center gap-2"
                >
                  <User className="w-4 h-4" />
                  {t('dashboard')}
                </Button>
              </motion.div>
            ) : (
              <>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    onClick={goToLogin}
                    className="bg-transparent border border-[#1e1e2e] text-gray-300 hover:bg-[#1e1e2e] hover:text-white px-4 py-2.5 rounded-xl text-sm flex items-center gap-2"
                  >
                    <LogIn className="w-4 h-4" />
                    {t('signIn')}
                  </Button>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    onClick={() => scrollToSection('calculator')}
                    className="btn-gold text-black font-semibold px-5 lg:px-6 py-2.5 rounded-xl text-sm"
                  >
                    {t('calculateWithGold')}
                  </Button>
                </motion.div>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-xl hover:bg-white/5 transition-colors relative z-50"
            aria-label="Toggle menu"
            whileTap={{ scale: 0.9 }}
          >
            <AnimatePresence mode="wait">
              {isMobileMenuOpen ? (
                <motion.div
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <X className="w-6 h-6 text-white" />
                </motion.div>
              ) : (
                <motion.div
                  key="menu"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <Menu className="w-6 h-6 text-white" />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>
        </div>

        {/* Mobile Navigation - Full Screen Overlay */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <>
              {/* Backdrop overlay */}
              <motion.div
                className="fixed inset-0 bg-[#0a0a0f]/95 backdrop-blur-md md:hidden"
                style={{ top: '64px' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsMobileMenuOpen(false)}
              />
              
              <motion.div 
                className="md:hidden overflow-hidden relative z-10 bg-[#0a0a0f]"
                variants={menuVariants}
                initial="closed"
                animate="open"
                exit="closed"
              >
                <motion.nav 
                  className="py-4 border-t border-white/10 flex flex-col gap-1 bg-[#0a0a0f]"
                >
                {navItems.map((item, index) => (
                  <motion.button
                    key={item.id}
                    variants={itemVariants}
                    onClick={() => scrollToSection(item.id)}
                    className="px-4 py-4 text-left text-gray-300 hover:text-white hover:bg-white/5 rounded-xl transition-all flex items-center gap-3 active:bg-white/10"
                  >
                    <span className="w-2 h-2 rounded-full bg-[#D4AF37]/50" />
                    {t(item.labelKey)}
                  </motion.button>
                ))}
                
                <motion.div 
                  variants={itemVariants}
                  className="px-4 pt-4 pb-2 space-y-3"
                >
                  {/* Language Selector for Mobile */}
                  <div className="flex items-center justify-between py-3 px-2 bg-[#12121a] rounded-xl border border-[#1e1e2e]">
                    <span className="text-sm text-gray-400">{t('language')}</span>
                    <LanguageSelector variant="minimal" dropdownPosition="top" />
                  </div>

                  {isAuthenticated ? (
                    <Button
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        navigate('/dashboard');
                      }}
                      className="w-full btn-gold text-black font-semibold py-4 rounded-xl text-base flex items-center justify-center gap-2"
                    >
                      <User className="w-5 h-5" />
                      {t('dashboard')}
                    </Button>
                  ) : (
                    <>
                      <Button
                        onClick={() => {
                          setIsMobileMenuOpen(false);
                          goToLogin();
                        }}
                        className="w-full bg-transparent border border-[#1e1e2e] text-gray-300 hover:bg-[#1e1e2e] py-4 rounded-xl text-base flex items-center justify-center gap-2"
                      >
                        <LogIn className="w-5 h-5" />
                        {t('signIn')}
                      </Button>
                      <Button
                        onClick={() => scrollToSection('calculator')}
                        className="w-full btn-gold text-black font-semibold py-4 rounded-xl text-base"
                      >
                        {t('calculateWithGold')}
                      </Button>
                    </>
                  )}
                </motion.div>
              </motion.nav>
            </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header;
